import {
  DEFAULT_MANNEQUIN_MODEL_ID,
  getMannequinManifestEntry,
  getMannequinVariantById,
  MANNEQUIN_VARIANTS,
} from "@poseref/shared";
import type { MannequinModel } from "./types.ts";

const BASE = (import.meta as any).env?.BASE_URL ?? "/";

/** All body type model IDs in display order. */
export const BODY_TYPES = MANNEQUIN_VARIANTS.map((variant) => variant.id);
export type BodyType = string;

/** Default model ID for new mannequins. */
export const DEFAULT_MODEL_ID: BodyType = DEFAULT_MANNEQUIN_MODEL_ID;

/**
 * Body types whose GLBs have been built and are available to load.
 * Derived from the generated shared mannequin manifest. Only variants
 * with a complete runtime contract are exposed to the UI.
 */
export const AVAILABLE_BODY_TYPES: readonly BodyType[] = BODY_TYPES.filter(
  (bt) => getMannequinManifestEntry(bt)?.runtimeReady === true,
);

function buildModel(id: string): MannequinModel {
  const variant = getMannequinVariantById(id);
  const manifest = getMannequinManifestEntry(id);
  const suffix = variant?.slug ?? id.replace("male-", "");
  const normalPath =
    variant?.hasNormalMap && manifest?.normal.present
      ? `${BASE}${manifest.normal.path}`
      : undefined;
  return {
    id,
    name: variant?.modelName ?? id,
    url: `${BASE}${manifest?.glb.path ?? `models/mannequin-${suffix}.glb`}`,
    morphs: [],
    textures: normalPath ? { normal: normalPath } : {},
  };
}

/**
 * Model definitions for all variants.
 *
 * URLs are static fallback paths. For production, use `useCachedModel`
 * from `modelCache.ts` which resolves assets through the persistent
 * IndexedDB cache (lazy-fetching from R2 on first load).
 */
export const MANNEQUIN_MODELS: Record<string, MannequinModel> = new Proxy(
  {} as Record<string, MannequinModel>,
  {
    get(_target, prop: string) {
      return buildModel(prop);
    },
    ownKeys() {
      return [...BODY_TYPES];
    },
    getOwnPropertyDescriptor(_, prop: string) {
      if (BODY_TYPES.includes(prop)) {
        return {
          configurable: true,
          enumerable: true,
          value: buildModel(prop),
        };
      }
      return undefined;
    },
  },
);
